import { render } from '@inlet/react-pixi';
import { boundMethod } from 'autobind-decorator';
import {
  Container,
  Graphics,
  InteractionEvent,
  Rectangle,
  Sprite,
  Text,
  Texture,
} from 'pixi.js';
import { createElement } from 'react';

import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { HighlightMatch } from 'flow-builder/hooks/useHighlight';
import {
  CanvasBaseComponent,
  Dependencies,
} from 'flow-builder/renderers/common/canvasBaseComponent';
import type { EditorContext } from 'flow-builder/types/context';
import { GenericNodeAttributes, HighlightType } from 'flow-builder/types/node';
import events, {
  CANVAS_CLICK,
  CANVAS_ZOOM,
  CanvasEventCallback,
} from 'flow-builder/utils/canvas/events/events';

import type { CanvasComponent } from '../../types/canvasComponent';
import type { Relation } from '../../types/relation';
import { getChildOffset } from '../../utils/canvas/calculateChildPosition/calculateChildPosition';
import { getIsNodeHighlighted } from '../../utils/node/getIsNodeHighlighted/getIsNodeHighlighted';
import { getIsNodeInTreeForId } from '../../utils/node/getNodeTree/getNodeTree';
import { purple, white } from '../common/colors';
import { styles as commonStyles } from '../common/commonStyles';
import type { Layers } from '../common/props';

import { NodeArrow } from './nodeArrowComponent';
import type { ArrowTextureMap } from './nodeArrowTextures';
import { styles } from './nodeStyles';
import type { NodePortTextureMap } from './nodeTextures';
import { DragListener } from './nodeUtils';

export interface Props {
  readonly node: Relation;
  readonly nodeAttributes: GenericNodeAttributes;
  readonly nodeComponentBase: Texture;
  readonly nodePortTextures: NodePortTextureMap;
  readonly arrowTextures: ArrowTextureMap;
  readonly layers: Layers;
  readonly editorContext: EditorContext;
  readonly isHighlighted: boolean;
}

enum SelectState {
  None = 'none',
  Before = 'before',
  After = 'after',
}

export class Node
  extends CanvasBaseComponent<Props>
  implements CanvasComponent<Props>
{
  public ref: Container;
  public props: Props;
  public name = 'Node';

  public editIconArea: Container;
  private content: Container;
  private baseSprite: Sprite;
  private ports: Container;
  private overlay: Graphics;
  private overlayText: Text;
  private shadowBox: Graphics;
  private contentBox: Graphics;
  private highlight: Graphics;

  public selectState: SelectState = SelectState.None;
  public nodeArrow: NodeArrow;
  private dragListener: DragListener | null = null;
  private isDragging = false;

  constructor(props: Props) {
    super(props);

    this.props = props;

    const {
      node,
      layers: { shadowFilterLayer },
      editorContext: {
        state: { viewState },
      },
      nodeComponentBase,
      nodeAttributes,
    } = this.props;
    const { id } = node;
    const hasActionModal = Reflect.has(nodeAttributes, 'ActionModal');
    const { label, isEditable = hasActionModal } = nodeAttributes;

    const nodeArrow = new NodeArrow(props);
    const wrapper = new Container();
    const shadowBox = new Graphics();
    const contentBox = new Graphics();
    const ports = new Container();
    const nodeBaseSprite = new Sprite(nodeComponentBase);
    const content = new Container();
    const edit = new Container();
    const highlight = new Graphics();
    const overlay = new Graphics();
    const overlayText = new Text('Attach +', {
      ...commonStyles.text,
      fontSize: 24,
      fill: white,
    });

    shadowBox.name = 'Shadow Box';
    shadowBox.parentLayer = shadowFilterLayer || undefined;

    contentBox.name = 'Content Box';

    ports.name = 'Ports';

    nodeBaseSprite.name = 'Base Sprite';
    nodeBaseSprite.anchor.set(0, 0);
    nodeBaseSprite.position.set(0, 0);

    edit.name = 'Edit Button Area';
    edit.buttonMode = true;
    edit.interactive = true;
    edit.hitArea = new Rectangle(styles.base.width - 26, 8, 16, 16);

    highlight.name = 'Highlight';
    highlight.visible = false;

    overlay.name = 'Overlay';
    overlay.visible = false;
    overlay.addChild(overlayText);

    overlayText.anchor.set(0.5, 0.5);

    content.name = 'Content';
    content.position.set(
      styles._content.padding,
      styles.head.height + styles._content.padding + styles._content.offset,
    );

    wrapper.interactive = true;
    wrapper.name = `${label} node - ${id}`;
    wrapper.addChild(shadowBox);
    wrapper.addChild(contentBox);
    wrapper.addChild(nodeBaseSprite);
    wrapper.addChild(content);
    wrapper.addChild(highlight);
    wrapper.addChild(nodeArrow.ref);
    wrapper.addChild(ports);
    wrapper.addChild(overlay);

    if (isEditable) {
      wrapper.addChild(edit);
    }

    this.ref = wrapper;
    this.nodeArrow = nodeArrow;
    this.baseSprite = nodeBaseSprite;
    this.content = content;
    this.shadowBox = shadowBox;
    this.contentBox = contentBox;
    this.ports = ports;
    this.editIconArea = edit;
    this.highlight = highlight;
    this.overlay = overlay;
    this.overlayText = overlayText;

    this.drawContent();
    this.addSelectListener();
    this.addContextListener();
    this.addDragListeners();
    this.addEditListener();
    this.drawPosition();
    this.drawContentBox();
    this.drawNodeHighlight();
    this.drawDisconnectFilter();
    this.drawNodePorts();

    super.setPropUpdateHandlers([
      this.addDragListeners,
      this.nodeArrow.updatePropsWithoutRedraw,
    ]);

    this.onZoomChange({ zoom: viewState.scale });

    events.subscribe(CANVAS_CLICK, this.onCanvasClick);
    events.subscribe(CANVAS_ZOOM, this.onZoomChange);

    super.updateProps(props);
  }

  componentWillUnmount() {
    this.unmount();
  }

  public get height(): number {
    const content = this.content;

    return content.height > 0
      ? content.height + styles._content.padding * 2 + styles.head.height + 5
      : styles.head.height;
  }

  private get isOrphan() {
    const { node } = this.props;
    const { parentId, nodeType } = node;

    return !parentId && nodeType.toLowerCase() !== FlowNodeTypes.INIT;
  }

  private get highlightColor() {
    const {
      editorContext: {
        state: {
          highlightState: { type },
        },
      },
    } = this.props;

    return commonStyles.highlight[type];
  }

  private get dragListenerProps(): DragListener['props'] {
    const { node, editorContext } = this.props;
    const container = this.ref;

    return {
      node,
      container,
      editorContext,
      onDragStart: () => (this.isDragging = true),
      onDragEnd: () => (this.isDragging = false),
    };
  }

  @boundMethod
  private addSelectListener() {
    const container = this.ref;

    container.on('click', this.onSelectClick);
  }

  @boundMethod
  private addContextListener() {
    const container = this.ref;

    container.on('rightclick', this.onContextMenuClick);
  }

  @boundMethod
  private addDragListeners() {
    const props = this.dragListenerProps;

    if (!this.dragListener) {
      const dragListener = new DragListener(props);

      dragListener.addListeners();

      this.dragListener = dragListener;
    } else {
      this.dragListener.props = props;
    }
  }

  @boundMethod
  private addEditListener() {
    const edit = this.editIconArea;

    edit.on('click', this.onEditClick);
  }

  private onCanvasClick: CanvasEventCallback<typeof CANVAS_CLICK> = () => {
    this.selectState = SelectState.None;
  };

  private onZoomChange: CanvasEventCallback<typeof CANVAS_ZOOM> = ({
    zoom,
  }) => {
    const affectedObjects: Container[] = [
      this.content,
      this.editIconArea,
      this.shadowBox,
      this.ports,
    ];

    if (zoom <= 0.3 && this.content.visible) {
      affectedObjects.forEach((obj) => (obj.visible = false));
    }

    if (zoom > 0.3 && !this.content.visible) {
      affectedObjects.forEach((obj) => (obj.visible = true));
    }
  };

  @boundMethod
  private onContextMenuClick(e: InteractionEvent) {
    const {
      node: { id },
      editorContext: {
        view: { viewport },
        state: { setHighlightState },
        handlers: {
          ui: { onContextMenuShow },
        },
      },
    } = this.props;

    if (!viewport) {
      return;
    }

    setHighlightState({
      only: [id],
    });

    viewport.plugins.pause('drag');
    onContextMenuShow(id, e);
    viewport.plugins.resume('drag');
  }

  @boundMethod
  private onSelectClick(event: InteractionEvent) {
    event.stopPropagation();

    const { node, editorContext } = this.props;
    const {
      state: { setHighlightState },
      view: { nodes },
    } = editorContext;
    const { id } = node;

    if (this.isDragging) {
      return;
    }

    /*
     * Reset all other node select state
     */
    Object.entries(nodes.current).forEach(([componentId, component]) => {
      if (componentId === id || !(component instanceof Node)) {
        return;
      }

      component.selectState = SelectState.None;
    });

    switch (this.selectState) {
      case SelectState.After:
      case SelectState.None:
        this.selectState = SelectState.Before;
        setHighlightState({
          path: [null, id],
          match: HighlightMatch.Connection,
          overrides: { [id]: { match: HighlightMatch.Both } },
        });
        break;
      case SelectState.Before:
        this.selectState = SelectState.After;
        setHighlightState({
          path: [id],
        });
        break;
    }
  }

  @boundMethod
  private onEditClick(event: InteractionEvent) {
    event.stopPropagation();

    const {
      node,
      editorContext: {
        handlers: {
          node: { onEdit },
        },
      },
    } = this.props;

    onEdit(node);
  }

  @boundMethod
  private drawPosition() {
    const {
      node: { pageX, pageY },
    } = this.props;
    const ref = this.ref;

    ref.position.set(pageX, pageY);
  }

  @boundMethod
  private drawNodeHighlight() {
    const highlight = this.highlight;
    const shadowBox = this.shadowBox;
    const { isHighlighted } = this.props;

    if (isHighlighted) {
      const { cornerRadius: radius } = styles.base;
      const width = shadowBox.width;
      const height = shadowBox.height;

      highlight.clear();
      highlight.visible = true;
      highlight.lineStyle(styles._highlight.size, this.highlightColor);
      highlight
        .moveTo(radius, 0)
        .arcTo(width, 0, width, 0 + radius, radius)
        .arcTo(width, height, width - radius, height, radius)
        .arcTo(0, height, 0, height - radius, radius)
        .arcTo(0, 0, 0 + radius, 0, radius);

      return;
    }

    highlight.clear();
    highlight.visible = false;
  }

  @boundMethod
  private drawBaseTexture() {
    const { nodeComponentBase } = this.props;
    const nodeBaseSprite = this.baseSprite;

    if (nodeBaseSprite.texture !== nodeComponentBase) {
      nodeBaseSprite.texture = nodeComponentBase;
    }
  }

  @boundMethod
  private drawContent() {
    const { node, editorContext, nodeAttributes } = this.props;
    const { NodeContent } = nodeAttributes;
    const {
      props: { flow },
      state: {
        nodeState: {
          scopeVars: { [node.id]: scopeVars },
        },
      },
    } = editorContext;
    const container = this.content;

    const element = createElement(NodeContent, {
      node,
      context: { extraData: flow.data },
      scopeVars,
    });

    render(element, container);
  }

  @boundMethod
  private drawNodePorts() {
    const { node, nodePortTextures, editorContext } = this.props;
    const {
      state: { highlightState },
    } = editorContext;
    const { connections } = node;
    const container = this.ports;

    const amountOfOuts = connections.length;

    container.removeChildren();
    container.position.set(0, this.height);
    connections.forEach(({ id, connection }, index) => {
      const isHighlighed = getIsNodeHighlighted(
        connection || id,
        highlightState.nodes,
        true,
      );
      const nodePortTexture =
        nodePortTextures[
          !isHighlighed ? HighlightType.NONE : highlightState.type
        ];
      const position = index + 1;
      const offset = getChildOffset(amountOfOuts, position);

      const port = new Sprite(nodePortTexture);

      port.anchor.set(0.5, 0.5);
      port.position.set(offset, 0);

      container.addChild(port);
    });
  }

  @boundMethod
  private drawContentBox() {
    const contentBox = this.contentBox;
    const shadowBox = this.shadowBox;
    const expectedHeight = this.height;

    const isContentBoxHeightEqual = shadowBox.height === contentBox.height;
    const isContentBoxHeightExpected = contentBox.height === expectedHeight;

    if (!isContentBoxHeightEqual || !isContentBoxHeightExpected) {
      contentBox.clear();
      shadowBox.clear();

      const fill = 0xffffff;
      const roundedRectArgs = [
        0,
        0,
        styles.base.width,
        expectedHeight,
        styles.base.cornerRadius,
      ] as const;

      contentBox.beginFill(fill);
      contentBox.drawRoundedRect(...roundedRectArgs);
      contentBox.endFill();

      shadowBox.beginFill(fill);
      shadowBox.drawRoundedRect(...roundedRectArgs);
      shadowBox.endFill();
    }
  }

  @boundMethod
  private drawDisconnectFilter() {
    const {
      node,
      editorContext,
      layers: { alphaFilterLayer },
    } = this.props;
    const {
      state: {
        nodeState: { rootTree },
        attachState: { hoveredNodeId },
      },
    } = editorContext;
    const { id } = node;
    const container = this.ref;

    const isInRootTree = rootTree.indexOf(id) !== -1;
    const isHovered = id === hoveredNodeId;

    if (!isInRootTree && !isHovered) {
      container.parentLayer = alphaFilterLayer || undefined;

      return;
    }

    container.parentLayer = undefined;
  }

  @boundMethod
  private drawAttachOverlay() {
    const { node, editorContext } = this.props;
    const {
      state: {
        nodeState: { relations },
        attachState,
      },
    } = editorContext;
    const isHovered = node.id === attachState.hoveredNodeId;
    const isDraggingRelevant =
      this.isOrphan &&
      attachState.isDragging &&
      !getIsNodeInTreeForId(node.id, attachState.actionNodeId, relations);

    const container = this.overlay;
    const text = this.overlayText;

    if (isHovered) {
      container.visible = true;
      text.visible = true;

      return;
    }

    if (isDraggingRelevant) {
      const height = this.height;

      container.clear();
      container.visible = true;
      container.beginFill(purple['500'], 0.8);
      container.drawRoundedRect(
        0,
        0,
        styles.base.width,
        height,
        styles.base.cornerRadius,
      );
      container.endFill();
      text.visible = false;
      text.position.set(styles.base.width / 2, height / 2);

      return;
    }

    container.visible = false;
  }

  protected getDependencies(props: Props): Dependencies<Props> {
    const { node, nodeComponentBase, editorContext, isHighlighted } = props;

    return [
      [
        [
          node.payload,
          editorContext.props.flow.data,
          editorContext.state.nodeState.scopeVars,
        ],
        [this.drawContent],
      ],
      [[this.height], [this.drawContentBox]],
      [[nodeComponentBase], [this.drawBaseTexture]],
      [
        [
          editorContext.state.attachState.isDragging,
          node.id === editorContext.state.attachState.hoveredNodeId,
        ],
        [this.drawAttachOverlay],
      ],
      [
        [
          editorContext.state.nodeState.rootTree.indexOf(node.id) !== -1,
          node.id === editorContext.state.attachState.hoveredNodeId,
        ],
        [this.drawDisconnectFilter],
      ],
      [[isHighlighted], [this.drawNodeHighlight]],
      [
        [
          node.pageX,
          node.pageY,
          node.connections.length,
          node.connections.filter(({ connection, id }) =>
            getIsNodeHighlighted(
              connection || id,
              editorContext.state.highlightState.nodes,
              true,
            ),
          ).length,
        ],
        [this.drawPosition, this.drawNodePorts],
      ],
      [[this.nodeArrow.isChanged(props)], [this.nodeArrow.redraw]],
    ];
  }

  public redraw(props: Props): void {
    super.redraw(props);
    this.updatePropsWithoutRedraw(props);
  }

  public updatePropsWithoutRedraw(props: Props): void {
    super.updateProps(props);
  }

  public unmount(): void {
    this.ref.destroy();
    this.nodeArrow.unmount();
    this.baseSprite.destroy();
    this.content.destroy();
    this.shadowBox.destroy();
    this.contentBox.destroy();
    this.ports.destroy();
    this.editIconArea.destroy();
    this.highlight.destroy();
    this.overlay.destroy();
    this.overlayText.destroy();

    events.unsubscribe(this.onCanvasClick);
    events.unsubscribe(this.onZoomChange);
    this.dragListener?.removeListeners();
    this.editIconArea.removeAllListeners();
  }
}
